import ColumnConfigurationPanel from "common/agGrid/ColumnConfigurationPanel";
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import ColumnConfiguration from "common/agGrid/columnConfiguration";
import { Button } from "react-bootstrap";
import PhysicalLocation, { AllRegionsHomeLocation, isSelfOrChild } from "domain/physicalLocation";
import { CommoditySettings, Region } from "domain/deliveryPeriod";
import ConfigurableColumnBuilder from "common/agGrid/configurableColumnBuilder";
import Commodity from "domain/commodity";
import { localizeBid as localize } from "features/localization/localizeService";


export interface GridLocationModalProps {
    isShowing: boolean;
    commodity: Commodity;
    locations: PhysicalLocation[];
    onClose(): void;
    selectedCommoditySettings: CommoditySettings[];
    saveRegionChanges(commodityRegions: CommoditySettings[]): any;
}

const GridLocationModal: React.FC<GridLocationModalProps> = (props: GridLocationModalProps) => {
    const [lastSelectedCommoditySettings, setLastSelectedCommoditySettings] = useState<CommoditySettings[]>(props.selectedCommoditySettings);
    const builder = new ConfigurableColumnBuilder();
    const commodityRegions = lastSelectedCommoditySettings.find(cr => cr.commodityId === props.commodity.id)?.regions;
    const selectedRegion = commodityRegions?.find(cr => cr.isSelected);
    const regionId = selectedRegion?.regionId ?? AllRegionsHomeLocation.id;
    const locations = props.locations.filter(l => l.commodities.some(c => c.id === props.commodity.id)
                        && isSelfOrChild(l, regionId));

    function getColumns(): ColumnConfiguration[] {
        locations.flatMap(x => builder.for(x.id, x.name).withWidth(50));
        return builder.listColumnConfigurations();
    }
    
    function listSelectedColumnConfigurations(): ColumnConfiguration[] {
        
        let locationIds: string[] = [];
        if(selectedRegion) {
            locationIds = selectedRegion.locationIds;
        }
        else {
            locationIds = locations.map(l => l.id);
        }
        return builder.listVisibleColumnConfigurations(locationIds);
    }

    function createRegion(columnConfigurations: ColumnConfiguration[]): Region {
        const region = new Region(regionId, true);
        region.locationIds = columnConfigurations.map(m => m.id);
        return region;
    }

    function onColumnsChanged(columnConfigurations: ColumnConfiguration[]) {
        if(!lastSelectedCommoditySettings || lastSelectedCommoditySettings.length === 0) {
            const region = createRegion(columnConfigurations);
            setLastSelectedCommoditySettings([{commodityId: props.commodity.id, regions: [region], selectedUnit: props.commodity.baseUnit}]);
        }
        else {
            const lastSelectedCommodityRegionsClone = [ ...lastSelectedCommoditySettings ];
            const index = lastSelectedCommodityRegionsClone.findIndex(x => x.commodityId === props.commodity.id);
            if(index === -1){
                const region = createRegion(columnConfigurations);
                lastSelectedCommodityRegionsClone.push({commodityId: props.commodity.id, regions: [region], selectedUnit: props.commodity.baseUnit});
            } 
            else {
                const regions = lastSelectedCommodityRegionsClone[index].regions;
                let region = regions.find(l => l.regionId === regionId);
                if(!region) {
                    region = createRegion(columnConfigurations);
                    regions.push(region);
                }
                else {
                    region.locationIds = columnConfigurations.map(m => m.id);
                }
            }
            setLastSelectedCommoditySettings(lastSelectedCommodityRegionsClone);
        }
    }

    
    function saveRegionChanges() {
        props.saveRegionChanges(lastSelectedCommoditySettings);  
        setLastSelectedCommoditySettings(null);
        props.onClose();
    }

    return <Modal show={props.isShowing} onHide={props.onClose} backdrop="static">
        <Modal.Header>
            <div className="row w-100">
                <div className="col-auto mr-auto">
                    <Modal.Title>{localize("GridLocationModal_Header_LocationConfig")}</Modal.Title>
                </div>
            </div>
        </Modal.Header>
        <Modal.Body>
            <ColumnConfigurationPanel
                columns={getColumns()}
                selectedColumns={listSelectedColumnConfigurations()}
                onColumnsChanged={(columns) => onColumnsChanged(columns)} />
        </Modal.Body>
        <Modal.Footer className="align center">
            <Button variant="secondary" onClick={props.onClose}>
                {localize("GridLocationModal_Button_Cancel")}
            </Button>
            <Button variant="primary" onClick={() => saveRegionChanges()}>
                {localize("GridLocationModal_Button_Save")}
            </Button>
        </Modal.Footer>
    </Modal>
}

export default GridLocationModal